// ui/src/pages/calendar/StaffCalendar.tsx
//
// The staff calendar (Task 21) — read-only, scoped to the classes the
// caller is assigned to. Staff never manage closures (that's admin-only,
// see AdminCalendar's <ClosureDialog>): this page renders no declare/edit/
// remove control, and `closure:write` is enforced server-side regardless.
//
// The classes shown are exactly what GET /calendar returns for a STAFF
// caller — derived server-side from the loaded user's assignments
// (unified-calendar handler, `classService.getClassesForStaff`). This page
// does NOT re-filter by class or location client-side: doing so would be
// security theatre (the server already enforces the slice) and could
// silently mask a server-side bug that over-returns classes.
//
// A closure is normal, planned operations, not an alarm — closed days use
// the same neutral slate styling CalendarDayCell already uses (no red).
//
// The one interactive affordance beyond navigation is the deep link into a
// session (RunSession, /sessions/:sessionId). Sessions are created lazily
// on first GET (`GET /sessions?classId=&date=`), so a future date has no
// session row — only today's and past class entries resolve a session id
// and get a link; future entries render as plain text rather than a dead
// link.
import React, { useMemo, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useScopedPath } from '../../components/ScopedLink';
import CalendarGrid from '../../components/calendar/CalendarGrid';
import type { CalendarDay, CalendarMode } from '../../components/calendar/types';
import { useGetCalendarQuery, useListClosuresQuery } from '../../store/api/localApi';
import { ClosureStatus } from '../../types';
import TodaySessionLink from './TodaySessionLink';

function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Monday-first index (0-6, Mon=0), matching CalendarGrid's own convention. */
function mondayFirstIndex(date: Date): number {
  return (date.getDay() + 6) % 7;
}

/** First/last day of the month containing `date`. */
function monthRange(date: Date): { from: string; to: string } {
  const first = new Date(date.getFullYear(), date.getMonth(), 1);
  const last = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return { from: toIsoDate(first), to: toIsoDate(last) };
}

/** Monday-to-Sunday week containing `date`. */
function weekRange(date: Date): { from: string; to: string } {
  const start = new Date(date);
  start.setDate(start.getDate() - mondayFirstIndex(date));
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return { from: toIsoDate(start), to: toIsoDate(end) };
}

/** The 7 days up to and including `date` — a trailing window guaranteed to
 *  contain a class day even when `date` itself falls on a weekend/closure,
 *  since no class here runs less than weekly. */
function trailingWeekRange(date: Date): { from: string; to: string } {
  const start = new Date(date);
  start.setDate(start.getDate() - 6);
  return { from: toIsoDate(start), to: toIsoDate(date) };
}

const StaffCalendar: React.FC = () => {
  const scopedPath = useScopedPath();
  // A STAFF membership is always location-scoped (there is no org-level
  // staff role), so activeLocationId is reliably the caller's one location —
  // unlike AdminCalendar, which needs a picker for ORG_ADMIN/SUPER_ADMIN.
  const { activeLocationId } = useAuth();

  const [mode, setMode] = useState<CalendarMode>('month');
  const [anchorDate, setAnchorDate] = useState(new Date());
  const todayIso = toIsoDate(new Date());

  const range = mode === 'month' ? monthRange(anchorDate) : weekRange(anchorDate);

  const {
    data: calendarData,
    isLoading: calendarLoading,
    isFetching: calendarFetching,
  } = useGetCalendarQuery({ from: range.from, to: range.to });

  // Independent, always-current window for the session deep-link panel —
  // decoupled from the visible grid's `range` so the panel still resolves
  // when the caller has navigated away from the current month/week, or
  // when today falls on a day none of the caller's classes run (see
  // `mostRecentClassDay` below).
  const recentWindow = trailingWeekRange(new Date());
  const { data: recentCalendarData } = useGetCalendarQuery({
    from: recentWindow.from,
    to: recentWindow.to,
  });

  // Location-level closures within the visible range, fetched directly
  // (not inferred from suppressed class entries) — a closure can cover a
  // day with ZERO scheduled classes, e.g. a Saturday inside a multi-day
  // closure range. `resolveSchedule` emits no row at all for a weekday a
  // class doesn't run, so a class-entry's own `suppressedBy: 'CLOSURE'`
  // would silently miss that day. AdminCalendar hits the same trap and
  // solves it the same way: a separate closures fetch keyed by date,
  // independent of whether any class happens to be scheduled.
  const { data: closuresData } = useListClosuresQuery(
    { locationId: activeLocationId ?? '', from: range.from, to: range.to },
    { skip: !activeLocationId }
  );

  const closureByDate = useMemo(() => {
    const map = new Map<string, string>();
    for (const closure of closuresData?.closures ?? []) {
      if (closure.status !== ClosureStatus.CLOSED) continue;
      let cursor = new Date(closure.startDate);
      const end = new Date(closure.endDate);
      while (cursor <= end) {
        map.set(toIsoDate(cursor), closure.label);
        cursor.setDate(cursor.getDate() + 1);
      }
    }
    return map;
  }, [closuresData]);

  const days: CalendarDay[] = useMemo(
    () =>
      (calendarData?.days ?? []).map((day) => ({
        date: day.date,
        classes: day.classes.map((entry) => ({
          classId: entry.classId,
          className: entry.className,
          startTime: entry.startTime,
          endTime: entry.endTime,
          suppressedBy: entry.suppressedBy,
          closureLabel: entry.closureLabel,
          overrideReason: entry.overrideReason,
        })),
        closureLabel: closureByDate.get(day.date),
      })),
    [calendarData, closureByDate]
  );

  // Distinct classes visible in the current range, in first-seen order —
  // feeds the "assigned classes" summary list (also exercises
  // calendar-class-name-{classId} for the e2e's "only assigned classes"
  // check without depending on any particular day being rendered).
  const assignedClasses = useMemo(() => {
    const seen = new Map<string, string>();
    for (const day of days) {
      for (const entry of day.classes) {
        if (!seen.has(entry.classId)) seen.set(entry.classId, entry.className);
      }
    }
    return [...seen.entries()].map(([classId, className]) => ({ classId, className }));
  }, [days]);

  // The most recent class day at or before today, from the independent
  // trailing-week window — the session deep-link target. A session row is
  // lazily created on first GET (see TodaySessionLink), so a future date
  // has no session to link to yet; rendering one would be a dead link
  // (addendum point 6). Not gated to literally "today" alone — today may
  // fall on a day none of the caller's classes run (e.g. a weekend), in
  // which case the most recent past class day is what "today's session"
  // sensibly means.
  const mostRecentClassDay = useMemo(
    () =>
      [...(recentCalendarData?.days ?? [])]
        .filter((d) => d.date <= todayIso && d.classes.some((c) => !c.suppressedBy))
        .sort((a, b) => (a.date < b.date ? 1 : -1))[0],
    [recentCalendarData, todayIso]
  );
  const linkableClasses = mostRecentClassDay?.classes.filter((c) => !c.suppressedBy) ?? [];
  const linkableDate = mostRecentClassDay?.date;

  function handleNavigate(direction: 'prev' | 'next' | 'today') {
    setAnchorDate((prev) => {
      if (direction === 'today') return new Date();
      const next = new Date(prev);
      if (mode === 'month') {
        next.setMonth(next.getMonth() + (direction === 'next' ? 1 : -1));
      } else {
        next.setDate(next.getDate() + (direction === 'next' ? 7 : -7));
      }
      return next;
    });
  }

  // Read-only: a day click has nothing to open (no closure dialog, no
  // editing) — CalendarGrid requires the prop, so this is a deliberate
  // no-op rather than an omission.
  function handleDayClick() {
    // intentionally empty — staff calendar is read-only
  }

  const closureDates = [...closureByDate.keys()].filter(
    (date) => date >= range.from && date <= range.to
  );

  return (
    <div className="py-6" data-testid="staff-calendar-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="md:flex md:items-center md:justify-between mb-6">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
              Calendar
            </h2>
            <p className="mt-1 text-sm text-gray-500">Schedule for your assigned classes</p>
          </div>
        </div>

        {assignedClasses.length > 0 && (
          <div className="mb-6 flex flex-wrap gap-2">
            {assignedClasses.map((c) => (
              <span
                key={c.classId}
                data-testid={`calendar-class-name-${c.classId}`}
                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700"
              >
                {c.className}
              </span>
            ))}
          </div>
        )}

        {linkableClasses.length > 0 && linkableDate && (
          <div className="mb-6 bg-white shadow rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-700">
                {linkableDate === todayIso ? 'Today' : `Most recent session (${linkableDate})`}
              </h3>
            </div>
            <ul className="divide-y divide-gray-200">
              {linkableClasses.map((entry) => (
                <TodaySessionLink
                  key={entry.classId}
                  classId={entry.classId}
                  className={entry.className}
                  startTime={entry.startTime}
                  endTime={entry.endTime}
                  date={linkableDate}
                  scopedPath={scopedPath}
                />
              ))}
            </ul>
          </div>
        )}

        {closureDates.length > 0 && (
          <div className="mb-6 bg-white shadow rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-700">Closures in view</h3>
            </div>
            <ul className="divide-y divide-gray-200">
              {closureDates.map((date) => (
                <li
                  key={date}
                  data-testid={`calendar-closure-note-${date}`}
                  className="px-4 py-3 text-sm"
                >
                  <span className="font-medium text-gray-900">{date}</span>
                  <span className="ml-2 text-slate-600">closed — {closureByDate.get(date)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {calendarLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            <p className="mt-2 text-sm text-gray-500">Loading calendar...</p>
          </div>
        ) : (
          <div
            data-testid="calendar-fetching"
            data-fetching={calendarFetching}
            className={calendarFetching ? 'opacity-60 transition-opacity' : ''}
          >
            <CalendarGrid
              mode={mode}
              days={days}
              onDayClick={handleDayClick}
              onNavigate={handleNavigate}
              onModeChange={setMode}
              dayAction={() => null}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffCalendar;
